import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useRef, useState, type KeyboardEvent, type TouchEvent } from 'react'
import type { Vehicle } from '../../data/vehicles'
import { Button } from '../../ui/Button'
import { useInventoryTranslation } from './useInventoryTranslation'
import { VehicleImage, vehicleLabel } from './VehicleImage'

export function VehicleGallery({ vehicle }: { vehicle: Vehicle }) {
  const { t } = useInventoryTranslation()
  const [selected, setSelected] = useState(0)
  const touchStart = useRef<number | null>(null)
  const count = vehicle.images.length

  useEffect(() => setSelected(0), [vehicle.id])

  const move = (direction: -1 | 1) => {
    if (count < 2) return
    setSelected((index) => (index + direction + count) % count)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      move(-1)
    } else if (event.key === 'ArrowRight') {
      event.preventDefault()
      move(1)
    } else if (event.key === 'Home') {
      event.preventDefault()
      setSelected(0)
    } else if (event.key === 'End' && count) {
      event.preventDefault()
      setSelected(count - 1)
    }
  }

  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    touchStart.current = event.changedTouches[0]?.clientX ?? null
  }

  const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const end = event.changedTouches[0]?.clientX
    if (touchStart.current === null || typeof end !== 'number') return
    const distance = end - touchStart.current
    touchStart.current = null
    if (Math.abs(distance) >= 40) move(distance < 0 ? 1 : -1)
  }

  return (
    <div
      className="vehicle-gallery"
      role="region"
      aria-label={t('galleryLabel', { vehicle: vehicleLabel(vehicle) })}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="vehicle-gallery__stage">
        <VehicleImage
          key={vehicle.images[selected]?.src ?? vehicle.id}
          vehicle={vehicle}
          image={vehicle.images[selected]}
          className="vehicle-gallery__image"
          eager
        />
        {count > 1 ? (
          <div className="vehicle-gallery__controls">
            <Button type="button" variant="secondary" size="icon" onClick={() => move(-1)} aria-label={t('galleryPrevious')}>
              <ChevronLeft aria-hidden="true" />
            </Button>
            <p aria-live="polite">{t('galleryCurrent', { current: selected + 1, count })}</p>
            <Button type="button" variant="secondary" size="icon" onClick={() => move(1)} aria-label={t('galleryNext')}>
              <ChevronRight aria-hidden="true" />
            </Button>
          </div>
        ) : null}
      </div>

      {count > 1 ? (
        <div className="vehicle-gallery__thumbs">
          {vehicle.images.map((image, index) => (
            <button
              key={image.src}
              className="vehicle-gallery__thumb"
              type="button"
              aria-label={t('gallerySelect', { current: index + 1, count })}
              aria-current={selected === index ? 'true' : undefined}
              onClick={() => setSelected(index)}
            >
              <VehicleImage vehicle={vehicle} image={image} className="vehicle-gallery__thumb-image" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
